import {
  Box,
  styled,
  Divider,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput } from '../../../../components/Input';
import { dimensions } from '../../../../styles/dimensions';
import { KeyBoard } from '../../../../types/keyboard';
import { useLocationStates } from '../store';
import { BLANK_STRING } from '../type';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import SearchIcon from '@mui/icons-material/Search';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { theme } from '../../../../styles/theme';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { PrimaryButton } from '../../../../components/Button';
import { Button } from '@mui/material';
import { Section } from '../../../../models/location';
import { showErrorToast, showSuccessToast } from '../../../../components/Toast/actions';

type Props = {
  isEditing?: boolean;
};

// Fix Leaflet marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

const MapComponent = ({
  onLocationSelect,
  defaultPosition,
  center
}: {
  onLocationSelect: (lat: number, lng: number) => void;
  defaultPosition?: [number, number];
  center?: [number, number];
}) => {
  const map = useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });

  React.useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);

  return defaultPosition ? <Marker position={defaultPosition} /> : null;
};

const RequiredAsterisk = styled('span')`
  color: ${theme.palette.error.main};
  margin-left: 4px;
`;

export const Body: React.FunctionComponent<Props> = ({ isEditing }: Props) => {
  const { t } = useTranslation();
  const { locationInfo } = useLocationStates();
  const isNumber = (value: string) => (value === null || value === '' ? false : !isNaN(Number(value)));

  const [selectedPosition, setSelectedPosition] = useState<[number, number] | undefined>(
    isEditing && locationInfo.latitude && locationInfo.longitude
      ? [locationInfo.latitude, locationInfo.longitude]
      : undefined
  );
  const [address, setAddress] = useState(isEditing ? locationInfo.address : '');
  const [city, setCity] = useState('');
  const [mapCenter, setMapCenter] = useState<[number, number] | undefined>(undefined);
  const [sections, setSections] = useState<Section[]>(isEditing && locationInfo.sections ? locationInfo.sections : []);

  // Nieuwe state voor de bevestigingsdialoog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<{
    lat: number;
    lng: number;
    address: string;
  } | null>(null);

  const handleLocationSelect = (lat: number, lng: number) => {
    // Haal eerst het adres op voordat we de bevestigingsdialoog tonen
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`)
      .then(response => response.json())
      .then(data => {
        const formattedAddress = formatAddress(data);

        // Sla de nieuwe locatie tijdelijk op en open de bevestigingsdialoog
        setPendingLocation({
          lat,
          lng,
          address: formattedAddress
        });
        setConfirmDialogOpen(true);
      })
      .catch(error => {
        console.error('Error fetching address:', error);
      });
  };

  // Functie om de locatiewijziging te bevestigen
  const confirmLocationChange = () => {
    if (!pendingLocation) return;

    const { lat, lng, address } = pendingLocation;

    // Update de positie en het adres
    setSelectedPosition([lat, lng]);
    setAddress(address);

    // Update hidden form fields
    const latInput = document.getElementsByName('latitude')[0] as HTMLInputElement;
    const lngInput = document.getElementsByName('longitude')[0] as HTMLInputElement;
    const addressInput = document.getElementsByName('address')[0] as HTMLInputElement;

    if (latInput) latInput.value = lat.toString();
    if (lngInput) lngInput.value = lng.toString();
    if (addressInput) addressInput.value = address;

    // Sluit de dialoog
    setConfirmDialogOpen(false);
    setPendingLocation(null);
  };

  // Functie om de locatiewijziging te annuleren
  const cancelLocationChange = () => {
    setConfirmDialogOpen(false);
    setPendingLocation(null);
  };

  const handleAddressSearch = async () => {
    try {
      // Controleer of er een stad is ingevuld bij het zoeken
      if (!city.trim()) {
        // Gebruik de bestaande toast functie
        showErrorToast({
          title: t('location.city.required_for_search'),
          subtitle: ''
        });
        return;
      }

      const searchQuery = `${address}, ${city}`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        const newPosition: [number, number] = [parseFloat(lat), parseFloat(lon)];
        setSelectedPosition(newPosition);
        setMapCenter(newPosition);

        // Formatteer het adres voor betere leesbaarheid
        const formattedAddress = formatAddress(data[0]);

        // Update het adresveld met het geformatteerde adres
        setAddress(formattedAddress);

        // Update hidden form fields
        const latInput = document.getElementsByName('latitude')[0] as HTMLInputElement;
        const lngInput = document.getElementsByName('longitude')[0] as HTMLInputElement;
        const addressInput = document.getElementsByName('address')[0] as HTMLInputElement;

        if (latInput) latInput.value = lat;
        if (lngInput) lngInput.value = lon;
        if (addressInput) addressInput.value = formattedAddress;
      } else {
        // Geen resultaten gevonden
        showErrorToast({
          title: t('location.address_not_found'),
          subtitle: ''
        });
      }
    } catch (error) {
      console.error('Error searching address:', error);
      showErrorToast({
        title: t('location.search_error'),
        subtitle: ''
      });
    }
  };

  // Verbeterde formatAddress functie
  const formatAddress = (result: any): string => {
    // Haal relevante delen uit het resultaat
    const street = result.address?.road || result.address?.street || '';
    const houseNumber = result.address?.house_number || '';
    const postcode = result.address?.postcode || '';
    const city = result.address?.city || result.address?.town || result.address?.village || '';

    // Controleer of we voldoende gegevens hebben
    const hasStreet = !!street;
    const hasCity = !!city;

    // Bouw het adres op in het gewenste formaat: straatnaam huisnummer, postcode stad
    let formattedAddress = '';

    // Straatnaam en huisnummer
    if (hasStreet) {
      formattedAddress += street;
      if (houseNumber) {
        formattedAddress += ` ${houseNumber}`;
      }
    }

    // Postcode en stad
    if (postcode || hasCity) {
      if (formattedAddress) formattedAddress += ', ';
      if (postcode) formattedAddress += postcode;
      if (postcode && hasCity) formattedAddress += ' ';
      if (hasCity) formattedAddress += city;
    }

    // Als we nog steeds geen goed adres hebben, probeer andere velden
    if (!formattedAddress || formattedAddress.length < 10) {
      // Probeer het display_name veld op te schonen
      if (result.display_name) {
        const parts = result.display_name.split(',').map((part: string) => part.trim());

        // Neem de eerste twee of drie delen, afhankelijk van de lengte
        if (parts.length >= 3) {
          return `${parts[0]}, ${parts[1]} ${parts[2]}`;
        } else if (parts.length === 2) {
          return parts.join(', ');
        } else {
          return parts[0];
        }
      }
    }

    return formattedAddress;
  };

  const handleAddSection = () => {
    const newSection: Section = {
      id: crypto.randomUUID(),
      name: ''
    };
    setSections([...sections, newSection]);
  };

  const handleRemoveSection = (id: string) => {
    setSections(sections.filter(section => section.id !== id));
  };

  const handleSectionNameChange = (id: string, newName: string) => {
    setSections(sections.map(section => (section.id === id ? { ...section, name: newName } : section)));
  };

  return (
    <ContentWrapper>
      <InputSection>
        <FormField>
          <Label>
            {t('location.location_name.label')}
            <RequiredAsterisk>*</RequiredAsterisk>
          </Label>
          <InputWrapper>
            <TextInput
              required={true}
              placeholder={t('location.location_name.placeholder')}
              inputwidth="100%"
              name="name"
              defaultValue={isEditing ? locationInfo.location : BLANK_STRING}
            />
          </InputWrapper>
        </FormField>

        <FormField>
          <Label>
            {t('location.address.label')}
            <RequiredAsterisk>*</RequiredAsterisk>
          </Label>
          <InputWrapper>
            <AddressWrapper>
              <TextInput
                required={true}
                placeholder={t('location.address.placeholder')}
                inputwidth="100%"
                name="address"
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </AddressWrapper>
          </InputWrapper>
        </FormField>

        <FormField>
          <Label>{t('location.city.label')}</Label>
          <InputWrapper>
            <AddressWrapper>
              <TextInput
                required={false}
                placeholder={t('location.city.placeholder')}
                inputwidth="100%"
                value={city}
                onChange={e => setCity(e.target.value)}
              />
              <SearchIconButton onClick={handleAddressSearch}>
                <SearchIcon fontSize="small" />
              </SearchIconButton>
            </AddressWrapper>
          </InputWrapper>
        </FormField>

        {/* Hidden inputs for coordinates */}
        <input type="hidden" name="latitude" defaultValue={selectedPosition?.[0].toString()} />
        <input type="hidden" name="longitude" defaultValue={selectedPosition?.[1].toString()} />

        <FormField>
          <Label>{t('location.min_emergency_team')}</Label>
          <InputWrapper>
            <TextInput
              placeholder="0"
              inputwidth="100%"
              name="minEmergencyTeam"
              defaultValue={isEditing ? locationInfo.minEmergencyTeam : BLANK_STRING}
              onKeyDown={e => {
                !isNumber(e.key) && e.key !== KeyBoard.BACKSPACE && e.preventDefault();
              }}
              onPaste={e => e.preventDefault()}
            />
          </InputWrapper>
        </FormField>

        <FormField>
          <Label>{t('location.emergency_team_persons')}</Label>
          <InputWrapper>
            <TextInput
              placeholder="0"
              inputwidth="100%"
              name="emergencyTeamPersons"
              defaultValue={isEditing ? locationInfo.emergencyTeamPersons : BLANK_STRING}
              onKeyDown={e => {
                !isNumber(e.key) && e.key !== KeyBoard.BACKSPACE && e.preventDefault();
              }}
              onPaste={e => e.preventDefault()}
            />
          </InputWrapper>
        </FormField>

        <MapWrapper>
          <MapContainer
            center={selectedPosition || [52.1326, 5.2913]}
            zoom={7}
            style={{ height: '100%', width: '100%' }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapComponent
              onLocationSelect={handleLocationSelect}
              defaultPosition={selectedPosition}
              center={mapCenter}
            />
          </MapContainer>
        </MapWrapper>

        <SectionsDivider />

        <SectionsHeader>
          <Typography variant="h6" color="grey.100">
            {t('location.sections.label')}
          </Typography>
        </SectionsHeader>

        <FormField>
          <Label>{t('location.sections.manage')}</Label>
          <InputWrapper>
            <SectionsContainer>
              {sections.map(section => (
                <SectionRow key={section.id}>
                  <TextInput
                    placeholder={t('location.sections.placeholder')}
                    inputwidth="100%"
                    value={section.name}
                    onChange={e => handleSectionNameChange(section.id, e.target.value)}
                  />
                  <DeleteIconButton onClick={() => handleRemoveSection(section.id)} size="small">
                    <DeleteIcon fontSize="small" />
                  </DeleteIconButton>
                </SectionRow>
              ))}
              <Button variant="outlined" onClick={handleAddSection} startIcon={<AddIcon />} fullWidth sx={{ mt: 1 }}>
                {t('location.sections.add')}
              </Button>
            </SectionsContainer>

            {/* Hidden input for sections data */}
            <input type="hidden" name="sections" value={JSON.stringify(sections)} />
          </InputWrapper>
        </FormField>
      </InputSection>

      <StyledDivider orientation="vertical" />

      <InfoSection>
        <Typography variant="h6" color="grey.100" gutterBottom>
          {t('location.calculation.title')}
        </Typography>

        <Typography variant="body1" color="grey.200" paragraph>
          {t('location.calculation.intro')}
        </Typography>

        <ExampleBox>
          <Typography variant="subtitle1" color="grey.100" gutterBottom>
            {t('location.calculation.example.title')}
          </Typography>
          <Typography variant="body2" color="grey.200">
            {t('location.calculation.example.text')}
          </Typography>
        </ExampleBox>

        <GuidelinesBox>
          <Typography variant="subtitle1" color="grey.100" gutterBottom>
            {t('location.calculation.guidelines.title')}
          </Typography>
          <Typography variant="body2" color="grey.200">
            {t('location.calculation.guidelines.text')}
          </Typography>
        </GuidelinesBox>

        <TipBox>
          <Typography variant="subtitle1" color="grey.100" gutterBottom>
            {t('location.calculation.tip.title')}
          </Typography>
          <Typography variant="body2" color="grey.200">
            {t('location.calculation.tip.text')}
          </Typography>
        </TipBox>

        <WarningBox>
          <Typography variant="subtitle1" color="grey.100" gutterBottom>
            {t('location.calculation.warning.title')}
          </Typography>
          <Typography variant="body2" color="grey.200">
            {t('location.calculation.warning.text')}
          </Typography>
        </WarningBox>
      </InfoSection>

      {/* Bevestigingsdialoog */}
      <Dialog open={confirmDialogOpen} onClose={cancelLocationChange}>
        <DialogTitle>{t('location.confirm_change')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{t('location.confirm_change_text')}:</Typography>
          <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
            {pendingLocation?.address}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={cancelLocationChange} color="inherit">
            {t('cancel')}
          </MuiButton>
          <MuiButton onClick={confirmLocationChange} color="primary" variant="contained">
            {t('confirm')}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </ContentWrapper>
  );
};

const { base, baseLG, baseMD } = dimensions;
const { grey } = theme.palette;

const ContentWrapper = styled(Box)`
  display: flex;
  width: 100%;
  gap: ${base};
  padding: ${base};
  max-width: 1800px;
  margin: 0 auto;
`;

const InputSection = styled(Box)`
  display: flex;
  flex-direction: column;
  row-gap: ${baseLG};
  width: 30%;
  min-width: 500px;
  margin-left: ${baseLG};
`;

const FormField = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${baseLG};
  width: 100%;
`;

const Label = styled(Box)`
  width: 170px;
  flex-shrink: 0;
`;

const InputWrapper = styled(Box)`
  flex: 1;
  position: relative;
`;

const StyledDivider = styled(Divider)`
  margin: 0 ${base};
  margin-left: 170px;
`;

const InfoSection = styled(Box)`
  flex: 1;
  padding: ${base};
  max-width: 50%;
  overflow-wrap: break-word;
  word-break: break-word;
  margin-right: ${baseLG};
  margin-left: -90px;
`;

const ExampleBox = styled(Box)`
  background-color: ${grey[500]};
  padding: ${base};
  border-radius: 4px;
  margin-bottom: ${base};
  max-width: 100%;
`;

const GuidelinesBox = styled(Box)`
  background-color: ${grey[500]};
  padding: ${base};
  border-radius: 4px;
  margin: ${base} 0;
  max-width: 100%;
`;

const TipBox = styled(Box)`
  border-left: 4px solid ${theme.palette.primary.main};
  padding-left: ${base};
  margin-top: ${base};
  max-width: 100%;
`;

const WarningBox = styled(Box)`
  border-left: 4px solid ${theme.palette.error.main};
  padding-left: ${base};
  margin-top: ${base};
  max-width: 100%;
`;

const AddressWrapper = styled(Box)`
  position: relative;
  width: 100%;
`;

const SearchIconButton = styled(IconButton)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px;
  &:hover {
    background-color: ${theme.palette.grey[100]};
  }
`;

const MapWrapper = styled(Box)`
  margin-top: ${baseMD};
  width: 100%;
  height: 300px;
  border-radius: 4px;
  overflow: hidden;
`;

const SectionsContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${dimensions.baseMD};
  width: 100%;
`;

const SectionRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${dimensions.base};
`;

const DeleteIconButton = styled(IconButton)`
  color: ${theme.palette.error.main};
  &:hover {
    background-color: ${theme.palette.error.light};
  }
`;

const SectionsDivider = styled(Divider)`
  margin: ${baseMD} 0;
  width: 100%;
`;

const SectionsHeader = styled(Box)`
  margin-bottom: ${baseMD};
`;
